import { FC } from 'react';

/**
 * The scratch-off reveal over the product grid (BPES-237).
 *
 * A dark coating over the product panel only -- the card's frame and the brokenplanet badge
 * are never covered -- scraped away by a chain of swipes. Starts opaque, ends fully revealed.
 *
 * A rect painted with the coating is masked by <mask>, where white keeps the coating and
 * black removes it. The mask holds a white rect plus black strokes, so animating a stroke's
 * stroke-dashoffset from 1 to 0 "draws" it and erases the coating along its length.
 * pathLength="1" renormalises each path, so that animation is the same 1 -> 0 whatever size
 * the panel renders at; the timing lives entirely in app.css.
 *
 * The rough edge is the point: a plain round-capped stroke reads as a wipe. #scratchRough
 * displaces each stroke with fractal noise so its edge comes out ragged, and punches
 * grooves through it so patches of coating survive as residue. Its scale and the groove
 * transfer below are the two dials if the texture needs revisiting.
 *
 * preserveAspectRatio="none" stretches the 100x60 viewBox to the panel, which is 1.69:1 on
 * desktop and squarer on a phone. Irregular strokes absorb that; a slice/meet fit would
 * instead crop the route and leave corners uncovered.
 */
export const ScratchReveal: FC = () => (
  // aria-hidden: purely decorative, and the products underneath are in the DOM and
  // reachable throughout — the overlay never gates content.
  <svg
    className="scratch-reveal"
    viewBox="0 0 100 60"
    preserveAspectRatio="none"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      {/*
        The coating colour, given by the client. Flat rather than a gradient: the reference
        is an even coating, and a ramp reads as a vignette that is not in it.

        Painted THROUGH #scratchCoatGrain below, so what reaches the screen is the filter's
        output rather than this value -- see the filter for why that matters.
      */}

      {/*
        Grain, so the coating reads as a material rather than a flat rectangle.

        ZERO-MEAN, and that is the point. Blending noise with "screen" only ever lightens: it
        took this coat to #8f8f8f, nearly three times too bright. An arithmetic composite
        computes k2*source + k3*noise + k4, and fractal noise averages 0.5, so k4 = -k3/2
        leaves the mean colour untouched and makes k3 purely the grain's amplitude.

        The colour matrix forces alpha to 1 first, so only luminance varies -- without it the
        noise's own alpha punches holes in the coating.
      */}
      <filter id="scratchCoatGrain" x="0" y="0" width="100%" height="100%">
        <feTurbulence
          type="fractalNoise"
          baseFrequency="1.4"
          numOctaves="2"
          seed="3"
          result="noise"
        />
        <feColorMatrix
          in="noise"
          type="matrix"
          result="grain"
          values="0.33 0.33 0.33 0 0
                  0.33 0.33 0.33 0 0
                  0.33 0.33 0.33 0 0
                  0    0    0    0 1"
        />
        <feComposite
          in="SourceGraphic"
          in2="grain"
          operator="arithmetic"
          k1="0"
          k2="1"
          k3="0.12"
          k4="-0.06"
        />
      </filter>

      {/*
        What makes this read as SCRAPING rather than wiping. Two stages, in order:

        1. RAGGED OUTLINE. Fractal noise displaces the stroke, so the boundary between
           scraped and unscraped is uneven and flaky instead of a clean round cap.

        2. GROOVES. A second, deliberately anisotropic turbulence -- a very low x frequency
           against a very high y one -- produces long horizontal streaks. Compositing the
           stroke "in" that mask punches those streaks out of it, so patches of coating
           survive inside the scratched area as residue, which is what the reference frame
           shows: its 75th-percentile pixel is #696a6c, a mid-grey, not the bare product
           underneath. A binary mask cannot produce that.

        The residue is safe to be this aggressive because it is temporary: the coating
        fades out at the end of the animation (scratch-reveal-clear in app.css), so the
        final state is fully revealed however much survives the scrape.

        feComponentTransfer is the contrast dial on the grooves. Raising "intercept"
        towards 0 leaves less residue; lowering it leaves more, and too low starts leaving
        whole products obscured for the length of the animation.
      */}
      <filter
        id="scratchRough"
        x="-30%"
        y="-30%"
        width="160%"
        height="160%"
        filterUnits="objectBoundingBox"
      >
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.11 0.19"
          numOctaves="3"
          seed="11"
          result="rough"
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="rough"
          scale="11"
          xChannelSelector="R"
          yChannelSelector="G"
          result="ragged"
        />

        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.045 0.3"
          numOctaves="3"
          seed="5"
          result="streak"
        />
        <feColorMatrix
          in="streak"
          type="luminanceToAlpha"
          result="streakAlpha"
        />
        <feComponentTransfer in="streakAlpha" result="grooves">
          <feFuncA type="linear" slope="2.4" intercept="-0.34" />
        </feComponentTransfer>

        <feComposite in="ragged" in2="grooves" operator="in" />
      </filter>

      <mask id="scratchMask" maskUnits="userSpaceOnUse">
        {/* White keeps the coating; the stroke below paints black to take it away. */}
        <rect x="0" y="0" width="100" height="60" fill="#ffffff" />
        {/*
          14 SWIPES, CHAINED END TO END.

          Every swipe starts at the exact point the previous one finished -- the coordinates
          come from a single route across the card, so the coin never teleports. That is the
          difference between this and a set of independent strokes: a hand stays where it
          left off.

          The route drifts from the top-left corner to the bottom-right, but works back on
          itself repeatedly on the way, which is why the swipes run at angles rather than
          cleanly left to right. Each is bowed slightly, alternating side, so none is a dead
          straight line.

          Because the chain itself wanders, the timing in app.css runs straight down this
          list -- swipe 2 follows swipe 1. It must: playing them out of order would break the
          continuity the route exists to create.

          Order here IS the scratch order, and app.css targets it with :nth-of-type. Adding
          or reordering a swipe re-times everything and needs a matching rule there.
        */}
        {/* swipe 1 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M -9 3 Q 10.1 11.9 31 15"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 2 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M 31 15 Q 49.4 5.7 70 4"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 3 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M 70 4 Q 85.8 15.2 105 17"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 4 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M 105 17 Q 81.6 24.9 57 27"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 5 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M 57 27 Q 35.9 18.1 13 17"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 6 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M 13 17 Q 4.7 28.2 -9 31"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 7 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M -9 31 Q 12.3 38.9 35 41"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 8 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M 35 41 Q 55.4 31.1 78 29"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 9 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M 78 29 Q 90.5 40.1 107 42"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 10 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M 107 42 Q 79.9 49 52 50"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 11 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M 52 50 Q 21.9 43 -9 44"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 12 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M -9 44 Q 10.4 46.4 26 58"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 13 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M 26 58 Q 49.6 56.4 72 49"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />

        {/* swipe 14 */}
        <path
          className="scratch-reveal__stroke"
          pathLength="1"
          d="M 72 49 Q 91.6 50.7 109 60"
          fill="none"
          stroke="#000000"
          strokeWidth="17"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#scratchRough)"
        />
      </mask>
    </defs>

    <g className="scratch-reveal__foil" mask="url(#scratchMask)">
      <rect
        x="0"
        y="0"
        width="100"
        height="60"
        fill="#353435"
        filter="url(#scratchCoatGrain)"
      />
    </g>
  </svg>
);
